/**
 * Credit, discount, and refund surfaces for the org Subscription rail.
 *
 * The house rule is credits first, refunds last, so the affordances are
 * weighted that way: credit is the primary action, refund lives on the
 * invoice row it applies to and asks you to type the amount before it fires.
 *
 * Two Stripe behaviours drive the design and are worth knowing before
 * editing this file:
 *
 *  - Credit is a property of the *customer*, not the subscription. It draws
 *    down against whichever invoice finalizes next and rolls over. There is
 *    no way to scope it to a plan.
 *  - Subscription discounts do NOT stack. Applying one replaces whatever was
 *    there, which is how a referral coupon vanishes without a trace. So the
 *    apply-discount dialog shows the incumbent and makes you confirm the
 *    replacement rather than discovering it later.
 */

import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';

import type {
  CreditTransactionRow,
  DiscountRow,
  InvoiceRow,
  ItemDiscountRow,
  SubscriptionLineItem,
} from './types';

const usd = (cents: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(cents / 100);

const date = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString() : '—');

/**
 * Sentinel for the whole-subscription discount target.
 *
 * Not the empty string: MUI's Select reads `''` as "nothing selected" and
 * renders the field blank, so the option can be listed but never shown as the
 * current value.
 */
const WHOLE_SUBSCRIPTION = '__whole_subscription__';

/** Cents from a free-text dollar amount. NaN when unparseable. */
function parseDollarsToCents(value: string): number {
  const cleaned = value.replace(/[$,\s]/g, '');
  if (!cleaned) return NaN;
  return Math.round(Number(cleaned) * 100);
}

/**
 * Shown instead of Edit/Remove when a discount wasn't applied from the panel.
 *
 * Referral coupons use IDs shared across organizations, so relabelling or
 * removing one here would affect every org carrying it. Bundle intro offers
 * carry eligibility accounting that removal wouldn't reset. The API refuses
 * both with a 403 — this keeps the UI from offering the action at all.
 */
function NotStaffAppliedChip() {
  return (
    <Tooltip title="Applied outside the admin panel — a referral coupon (shared across organizations) or an automatic bundle offer. Change it at the source.">
      <Chip label="Not editable here" size="small" variant="outlined" sx={{ height: 20 }} />
    </Tooltip>
  );
}

/** Human-readable summary of a discount, e.g. "50% off for 3 months". */
export function describeDiscount(discount: DiscountRow): string {
  const magnitude =
    discount.percentOff != null
      ? `${discount.percentOff}% off`
      : discount.amountOffCents != null
        ? `${usd(discount.amountOffCents)} off`
        : 'discount';

  if (discount.duration === 'forever') return `${magnitude}, forever`;
  if (discount.duration === 'repeating' && discount.durationInMonths) {
    return `${magnitude} for ${discount.durationInMonths} months`;
  }
  return `${magnitude}, once`;
}

// ---------------------------------------------------------------- credits

export function CreditBalanceCard({
  creditBalanceCents,
  transactions,
  onApplyCredit,
}: {
  creditBalanceCents: number | null;
  transactions: CreditTransactionRow[];
  onApplyCredit?: () => void;
}) {
  const hasCustomer = creditBalanceCents != null;
  const balance = creditBalanceCents ?? 0;

  return (
    <section className="rounded-lg border border-gray-200 bg-white p-4">
      <div className="mb-3 flex items-center justify-between">
        <h3 className="text-sm font-semibold text-gray-900">Credit balance</h3>
        {onApplyCredit && (
          <Button size="small" variant="contained" disabled={!hasCustomer} onClick={onApplyCredit}>
            Apply credit
          </Button>
        )}
      </div>

      {!hasCustomer ? (
        <Typography variant="body2" color="text.secondary">
          No Stripe customer — there is nowhere to hold credit yet.
        </Typography>
      ) : (
        <>
          <div className="flex items-baseline gap-2">
            <span
              className={`text-2xl font-semibold ${balance > 0 ? 'text-green-700' : balance < 0 ? 'text-red-700' : 'text-gray-400'}`}
            >
              {usd(Math.abs(balance))}
            </span>
            <span className="text-sm text-gray-500">
              {balance > 0 ? 'available' : balance < 0 ? 'owed' : 'no credit'}
            </span>
          </div>
          <p className="mt-1 text-xs text-gray-500">
            Draws down automatically against the next invoice. Unused credit rolls over.
          </p>

          {transactions.length > 0 && (
            <div className="mt-3 divide-y divide-gray-100 border-t border-gray-100 pt-2">
              {transactions.map((txn) => (
                <div key={txn.id} className="flex items-baseline gap-3 py-1.5 text-sm">
                  <span className="w-24 shrink-0 text-gray-500">{date(txn.createdAt)}</span>
                  <span className="flex-1 text-gray-700">{txn.description ?? '—'}</span>
                  <span
                    className={`w-24 shrink-0 text-right font-medium ${txn.amountCents > 0 ? 'text-green-700' : 'text-red-700'}`}
                  >
                    {txn.amountCents > 0 ? '+' : '−'}
                    {usd(Math.abs(txn.amountCents))}
                  </span>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </section>
  );
}

export function ApplyCreditDialog({
  open,
  organizationName,
  isSubmitting = false,
  onClose,
  onSubmit,
}: {
  open: boolean;
  organizationName: string;
  isSubmitting?: boolean;
  onClose: () => void;
  onSubmit: (amountCents: number, reason: string) => void;
}) {
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');

  useEffect(() => {
    if (open) {
      setAmount('');
      setReason('');
    }
  }, [open]);

  const cents = parseDollarsToCents(amount);
  const amountValid = Number.isFinite(cents) && cents !== 0;
  const canSubmit = amountValid && reason.trim().length >= 3 && !isSubmitting;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Apply credit</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Adds credit to {organizationName}&apos;s account. It draws down against the next
          invoice and rolls over until used. Use a negative amount to take credit back.
        </Typography>

        <Stack spacing={2}>
          <TextField
            label="Amount"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            placeholder="130.00"
            autoFocus
            fullWidth
            slotProps={{ input: { startAdornment: <span className="mr-1 text-gray-500">$</span> } }}
            error={amount.length > 0 && !amountValid}
            helperText={
              amount.length > 0 && !amountValid
                ? 'Enter a non-zero dollar amount'
                : amountValid && cents < 0
                  ? `Takes back ${usd(Math.abs(cents))}`
                  : ' '
            }
          />
          <TextField
            label="Reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Welcome back offer"
            fullWidth
            multiline
            minRows={2}
            helperText="Recorded in Stripe and in the audit trail"
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={!canSubmit}
          onClick={() => onSubmit(cents, reason.trim())}
        >
          {cents < 0 ? 'Take back credit' : 'Apply credit'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// -------------------------------------------------------------- discounts

export function DiscountCard({
  discounts,
  itemDiscounts = [],
  hasSubscription,
  onApplyDiscount,
  onRemoveDiscount,
  onRemoveItemDiscount,
  onEditDiscount,
}: {
  discounts: DiscountRow[];
  itemDiscounts?: ItemDiscountRow[];
  hasSubscription: boolean;
  onApplyDiscount?: () => void;
  onRemoveDiscount?: (discount: DiscountRow) => void;
  onRemoveItemDiscount?: (discount: ItemDiscountRow) => void;
  onEditDiscount?: (discount: DiscountRow) => void;
}) {
  return (
    <section className="rounded-lg border border-gray-200 bg-white p-4">
      <div className="mb-3 flex items-center justify-between">
        <h3 className="text-sm font-semibold text-gray-900">Discount</h3>
        {onApplyDiscount && (
          <Button
            size="small"
            variant="outlined"
            disabled={!hasSubscription}
            onClick={onApplyDiscount}
          >
            Apply discount
          </Button>
        )}
      </div>

      {!hasSubscription ? (
        <Typography variant="body2" color="text.secondary">
          No subscription — a discount needs something to apply to.
        </Typography>
      ) : (
        <>
          {discounts.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No subscription discount applied.
            </Typography>
          ) : (
            <div className="divide-y divide-gray-100">
              {discounts.map((discount) => (
                <div
                  key={discount.id ?? discount.couponId}
                  className="flex items-center gap-3 py-1.5 text-sm"
                >
                  <div className="flex-1">
                    <div className="font-medium text-gray-900">
                      {describeDiscount(discount)}
                    </div>
                    <div className="text-xs text-gray-500">
                      {discount.reason ?? 'No reason recorded'}
                      {discount.endsAt && ` · ends ${date(discount.endsAt)}`}
                    </div>
                    {/* Staff otherwise have to open Stripe to find out what
                        the customer is actually shown. */}
                    {discount.couponName && (
                      <div className="text-xs text-gray-400">
                        Customer sees “{discount.couponName}”
                      </div>
                    )}
                  </div>
                  {discount.isAdminApplied ? (
                    <>
                      {onEditDiscount && (
                        <Button size="small" onClick={() => onEditDiscount(discount)}>
                          Edit labels
                        </Button>
                      )}
                      {onRemoveDiscount && (
                        <Button
                          size="small"
                          color="error"
                          onClick={() => onRemoveDiscount(discount)}
                        >
                          Remove
                        </Button>
                      )}
                    </>
                  ) : (
                    <NotStaffAppliedChip />
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Per-item offers are applied automatically on bundle assignment
              and are untouched by the subscription-level action above, so
              they're listed for awareness rather than as something to manage. */}
          {itemDiscounts.length > 0 && (
            <div className="mt-3 border-t border-gray-100 pt-3">
              <div className="mb-1.5 text-xs font-medium uppercase tracking-wide text-gray-400">
                Line item discounts
              </div>
              <div className="divide-y divide-gray-100">
                {itemDiscounts.map((discount) => (
                  <div
                    key={discount.subscriptionItemId ?? discount.id ?? discount.couponId}
                    className="flex items-center gap-3 py-1.5 text-sm"
                  >
                    <div className="flex-1">
                      <div className="text-gray-900">
                        {discount.productName ?? 'Line item'} — {describeDiscount(discount)}
                      </div>
                      <div className="text-xs text-gray-500">
                        {discount.reason ?? 'Scoped to this line'}
                        {discount.endsAt && ` · ends ${date(discount.endsAt)}`}
                      </div>
                      {discount.couponName && (
                        <div className="text-xs text-gray-400">
                          Customer sees “{discount.couponName}”
                        </div>
                      )}
                    </div>
                    {discount.isAdminApplied ? (
                      <>
                        {onEditDiscount && (
                          <Button size="small" onClick={() => onEditDiscount(discount)}>
                            Edit labels
                          </Button>
                        )}
                        {onRemoveItemDiscount && (
                          <Button
                            size="small"
                            color="error"
                            onClick={() => onRemoveItemDiscount(discount)}
                          >
                            Remove
                          </Button>
                        )}
                      </>
                    ) : (
                      <NotStaffAppliedChip />
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </section>
  );
}

export function ApplyDiscountDialog({
  open,
  organizationName,
  existingDiscounts,
  itemDiscounts = [],
  lineItems = [],
  isSubmitting = false,
  onClose,
  onSubmit,
}: {
  open: boolean;
  organizationName: string;
  existingDiscounts: DiscountRow[];
  itemDiscounts?: ItemDiscountRow[];
  lineItems?: SubscriptionLineItem[];
  isSubmitting?: boolean;
  onClose: () => void;
  onSubmit: (input: {
    percentOff: number | null;
    amountOffCents: number | null;
    duration: 'once' | 'repeating' | 'forever';
    durationInMonths: number | null;
    reason: string;
    customerLabel: string;
    subscriptionItemId: string | null;
    replaceExisting: boolean;
  }) => void;
}) {
  const [kind, setKind] = useState<'percent' | 'amount'>('percent');
  const [value, setValue] = useState('');
  const [duration, setDuration] = useState<'once' | 'repeating' | 'forever'>('once');
  const [months, setMonths] = useState('3');
  const [reason, setReason] = useState('');
  // Kept separate from the reason on purpose: reasons are written for us
  // ("closing the deal", "he was upset") and must never reach a customer.
  const [customerLabel, setCustomerLabel] = useState('Discount');
  const [acknowledgedReplace, setAcknowledgedReplace] = useState(false);
  // Defaults to the whole subscription: "20% off everything" is still the
  // common ask, and making people pick a line every time is friction.
  const [targetItemId, setTargetItemId] = useState<string>(WHOLE_SUBSCRIPTION);

  // The incumbent depends on where this is aimed — an item discount can only
  // displace another discount on that same line.
  const targetsWholeSubscription = targetItemId === WHOLE_SUBSCRIPTION;

  const incumbent = targetsWholeSubscription
    ? existingDiscounts[0]
    : itemDiscounts.find((d) => d.subscriptionItemId === targetItemId);

  const targetLabel = targetsWholeSubscription
    ? organizationName
    : (lineItems.find((i) => i.subscriptionItemId === targetItemId)?.productName ??
      'this line');

  useEffect(() => {
    if (open) {
      setKind('percent');
      setValue('');
      setDuration('once');
      setMonths('3');
      setReason('');
      setCustomerLabel('Discount');
      setAcknowledgedReplace(false);
      setTargetItemId(WHOLE_SUBSCRIPTION);
    }
  }, [open]);

  // Changing target changes what's at risk, so a stale acknowledgement
  // must not carry over.
  useEffect(() => {
    setAcknowledgedReplace(false);
  }, [targetItemId]);

  const percentOff = kind === 'percent' ? Number(value) : null;
  const amountOffCents = kind === 'amount' ? parseDollarsToCents(value) : null;

  const valueValid =
    kind === 'percent'
      ? Number.isFinite(percentOff) && (percentOff ?? 0) >= 1 && (percentOff ?? 0) <= 100
      : Number.isFinite(amountOffCents) && (amountOffCents ?? 0) > 0;

  const monthsValid = duration !== 'repeating' || Number(months) >= 1;
  const replaceSatisfied = !incumbent || acknowledgedReplace;
  const canSubmit =
    valueValid &&
    monthsValid &&
    reason.trim().length >= 3 &&
    customerLabel.trim().length > 0 &&
    replaceSatisfied &&
    !isSubmitting;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Apply discount</DialogTitle>
      <DialogContent>
        {incumbent && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            <strong>{targetLabel} already has a discount:</strong>{' '}
            {describeDiscount(incumbent)} ({incumbent.couponName ?? incumbent.couponId}).
            <br />
            Discounts do not stack at the same level — applying this one will{' '}
            <strong>replace</strong> it.
          </Alert>
        )}

        <Stack spacing={2}>
          {lineItems.length > 0 && (
            <TextField
              select
              label="Apply to"
              value={targetItemId}
              onChange={(e) => setTargetItemId(e.target.value)}
              fullWidth
              helperText={
                targetsWholeSubscription
                  ? 'Discounts the whole bill'
                  : 'Line discounts stack with the subscription discount and with other lines'
              }
            >
              <MenuItem value={WHOLE_SUBSCRIPTION}>Whole subscription</MenuItem>
              {lineItems.map((item) => (
                <MenuItem key={item.subscriptionItemId} value={item.subscriptionItemId}>
                  {item.productName ?? 'Line item'}
                  {item.quantity != null && ` × ${item.quantity}`}
                  {item.unitAmountCents != null && ` (${usd(item.unitAmountCents)} each)`}
                  {item.hasDiscount && ' — already discounted'}
                </MenuItem>
              ))}
            </TextField>
          )}

          <ToggleButtonGroup
            value={kind}
            exclusive
            size="small"
            onChange={(_, next) => {
              if (next) {
                setKind(next);
                setValue('');
              }
            }}
          >
            <ToggleButton value="percent">Percentage</ToggleButton>
            <ToggleButton value="amount">Fixed amount</ToggleButton>
          </ToggleButtonGroup>

          <TextField
            label={kind === 'percent' ? 'Percent off' : 'Amount off'}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder={kind === 'percent' ? '50' : '130.00'}
            autoFocus
            fullWidth
            slotProps={{
              input:
                kind === 'percent'
                  ? { endAdornment: <span className="ml-1 text-gray-500">%</span> }
                  : { startAdornment: <span className="mr-1 text-gray-500">$</span> },
            }}
            error={value.length > 0 && !valueValid}
            helperText={
              value.length > 0 && !valueValid
                ? kind === 'percent'
                  ? 'Enter a percentage between 1 and 100'
                  : 'Enter a positive dollar amount'
                : ' '
            }
          />

          <TextField
            select
            label="Duration"
            value={duration}
            onChange={(e) => setDuration(e.target.value as 'once' | 'repeating' | 'forever')}
            fullWidth
          >
            <MenuItem value="once">Once — next invoice only</MenuItem>
            <MenuItem value="repeating">Repeating — a set number of months</MenuItem>
            <MenuItem value="forever">Forever — every invoice</MenuItem>
          </TextField>

          {duration === 'repeating' && (
            <TextField
              label="Months"
              value={months}
              onChange={(e) => setMonths(e.target.value)}
              type="number"
              fullWidth
              error={!monthsValid}
              helperText={!monthsValid ? 'Enter at least 1 month' : ' '}
            />
          )}

          <TextField
            label="Reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Closing the annual deal"
            fullWidth
            multiline
            minRows={2}
            helperText="Internal. Recorded in the audit trail — the customer never sees this"
          />

          <TextField
            label="Label on customer invoice"
            value={customerLabel}
            onChange={(e) => setCustomerLabel(e.target.value)}
            fullWidth
            slotProps={{ htmlInput: { maxLength: 40 } }}
            error={customerLabel.trim().length === 0}
            helperText={
              customerLabel.trim().length === 0
                ? 'Required — this appears on their Stripe invoice'
                : 'What the customer sees next to the deduction. Keep it neutral.'
            }
          />

          {incumbent && (
            <label className="flex cursor-pointer items-start gap-2 rounded border border-amber-200 bg-amber-50 p-2 text-sm text-amber-900">
              <input
                type="checkbox"
                className="mt-0.5"
                checked={acknowledgedReplace}
                onChange={(e) => setAcknowledgedReplace(e.target.checked)}
              />
              <span>
                I understand this replaces the existing {describeDiscount(incumbent)} discount
                on {targetLabel}.
              </span>
            </label>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={!canSubmit}
          onClick={() =>
            onSubmit({
              percentOff: kind === 'percent' ? Number(value) : null,
              amountOffCents: kind === 'amount' ? parseDollarsToCents(value) : null,
              duration,
              durationInMonths: duration === 'repeating' ? Number(months) : null,
              reason: reason.trim(),
              customerLabel: customerLabel.trim(),
              subscriptionItemId: targetsWholeSubscription ? null : targetItemId,
              replaceExisting: Boolean(incumbent),
            })
          }
        >
          {incumbent ? 'Replace discount' : 'Apply discount'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ---------------------------------------------------------------- refunds

/**
 * Refund state for one invoice row, derived once so the row and dialog agree.
 *
 * The ceiling is the CHARGE amount, not the invoice total. When customer
 * credit is applied, the charge collects only the remainder — deriving from
 * the invoice total would offer a refund larger than the money that moved.
 */
export function refundStateFor(invoice: InvoiceRow) {
  const refunded = invoice.amountRefundedCents;
  const charged = invoice.chargeAmountCents;
  const refundable = invoice.chargeId != null ? (charged ?? 0) - (refunded ?? 0) : 0;

  return {
    /** Refund state was loaded (admin surface), as opposed to unknown. */
    known: refunded != null && charged != null,
    chargedCents: charged ?? 0,
    refundedCents: refunded ?? 0,
    refundableCents: Math.max(0, refundable),
    canRefund: invoice.chargeId != null && invoice.status === 'paid' && refundable > 0,
    fullyRefunded: refunded != null && refunded > 0 && refundable <= 0,
  };
}

export function RefundDialog({
  open,
  invoice,
  isSubmitting = false,
  onClose,
  onSubmit,
}: {
  open: boolean;
  invoice: InvoiceRow | null;
  isSubmitting?: boolean;
  onClose: () => void;
  onSubmit: (input: {
    chargeId: string;
    amountCents: number;
    reason: 'duplicate' | 'fraudulent' | 'requested_by_customer';
    note: string;
  }) => void;
}) {
  const state = useMemo(
    () => (invoice ? refundStateFor(invoice) : null),
    [invoice],
  );

  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState<'duplicate' | 'fraudulent' | 'requested_by_customer'>(
    'requested_by_customer',
  );
  const [note, setNote] = useState('');
  const [confirmAmount, setConfirmAmount] = useState('');

  useEffect(() => {
    if (open && state) {
      // Prefill the full remaining amount — the common case is a full refund,
      // and starting from the real number makes a partial an explicit edit.
      setAmount((state.refundableCents / 100).toFixed(2));
      setReason('requested_by_customer');
      setNote('');
      setConfirmAmount('');
    }
  }, [open, state]);

  if (!invoice || !state) return null;

  const cents = parseDollarsToCents(amount);
  const amountValid = Number.isFinite(cents) && cents > 0 && cents <= state.refundableCents;

  // Typing the amount rather than a fixed word forces the number to be read.
  const confirmMatches =
    amountValid && Math.abs(parseDollarsToCents(confirmAmount) - cents) === 0;
  const canSubmit = amountValid && confirmMatches && note.trim().length >= 3 && !isSubmitting;

  const isPartial = amountValid && cents < state.refundableCents;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Refund invoice {invoice.number ?? invoice.id}</DialogTitle>
      <DialogContent>
        <Alert severity="info" sx={{ mb: 2 }}>
          A refund returns money and nothing else. It does not cancel the subscription or stop
          future billing.
        </Alert>

        <div className="mb-3 rounded border border-gray-200 bg-gray-50 p-3 text-sm">
          <div className="flex justify-between">
            <span className="text-gray-600">Invoice total</span>
            <span className="font-medium">{usd(invoice.totalCents)}</span>
          </div>
          {/* Only differs from the invoice total when credit covered part of
              it. Showing both makes the gap legible instead of surprising. */}
          {state.chargedCents !== invoice.totalCents && (
            <div className="flex justify-between text-gray-600">
              <span>Paid by card</span>
              <span className="font-medium">{usd(state.chargedCents)}</span>
            </div>
          )}
          {state.refundedCents > 0 && (
            <div className="flex justify-between text-amber-800">
              <span>Already refunded</span>
              <span className="font-medium">−{usd(state.refundedCents)}</span>
            </div>
          )}
          <div className="mt-1 flex justify-between border-t border-gray-200 pt-1">
            <span className="text-gray-600">Still refundable</span>
            <span className="font-semibold">{usd(state.refundableCents)}</span>
          </div>
        </div>

        <Stack spacing={2}>
          <TextField
            label="Refund amount"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            fullWidth
            autoFocus
            slotProps={{ input: { startAdornment: <span className="mr-1 text-gray-500">$</span> } }}
            error={amount.length > 0 && !amountValid}
            helperText={
              amount.length > 0 && !amountValid
                ? `Enter an amount between $0.01 and ${usd(state.refundableCents)}`
                : isPartial
                  ? `Partial refund — ${usd(state.refundableCents - cents)} stays charged`
                  : 'Full refund of the remaining amount'
            }
          />

          <TextField
            select
            label="Reason"
            value={reason}
            onChange={(e) =>
              setReason(e.target.value as 'duplicate' | 'fraudulent' | 'requested_by_customer')
            }
            fullWidth
          >
            <MenuItem value="requested_by_customer">Requested by customer</MenuItem>
            <MenuItem value="duplicate">Duplicate charge</MenuItem>
            <MenuItem value="fraudulent">Fraudulent</MenuItem>
          </TextField>

          <TextField
            label="Note"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Double charged in July after the Stripe migration"
            fullWidth
            multiline
            minRows={2}
            helperText="Stripe only accepts its own reason categories, so the detail lives here"
          />

          <TextField
            label={`Type ${amountValid ? usd(cents) : 'the amount'} to confirm`}
            value={confirmAmount}
            onChange={(e) => setConfirmAmount(e.target.value)}
            fullWidth
            disabled={!amountValid}
            slotProps={{ input: { startAdornment: <span className="mr-1 text-gray-500">$</span> } }}
            error={confirmAmount.length > 0 && !confirmMatches}
            helperText={
              confirmAmount.length > 0 && !confirmMatches
                ? 'Does not match the refund amount'
                : 'Refunds cannot be undone'
            }
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="error"
          disabled={!canSubmit}
          onClick={() =>
            onSubmit({
              chargeId: invoice.chargeId!,
              amountCents: cents,
              reason,
              note: note.trim(),
            })
          }
        >
          Refund {amountValid ? usd(cents) : ''}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/** Refund affordance for an invoice row: a button, a badge, or nothing. */
export function InvoiceRefundAction({
  invoice,
  onRefund,
}: {
  invoice: InvoiceRow;
  onRefund?: (invoice: InvoiceRow) => void;
}) {
  const state = refundStateFor(invoice);

  if (!state.known) return null;

  if (state.fullyRefunded) {
    return <Chip label="Refunded" size="small" color="warning" variant="outlined" sx={{ height: 20 }} />;
  }

  if (state.refundedCents > 0) {
    return (
      <Tooltip title={`${usd(state.refundedCents)} refunded, ${usd(state.refundableCents)} remaining`}>
        <span>
          <Button size="small" color="error" onClick={() => onRefund?.(invoice)}>
            Refund…
          </Button>
        </span>
      </Tooltip>
    );
  }

  if (!state.canRefund) return null;

  return (
    <Button size="small" color="error" onClick={() => onRefund?.(invoice)}>
      Refund…
    </Button>
  );
}

/**
 * Edit a discount's labels without touching what it is worth.
 *
 * Stripe treats a discount's amount and duration as immutable, so this
 * deliberately offers only the two labels — changing the value is a
 * remove-and-reapply, which the card handles separately.
 */
export function EditDiscountLabelsDialog({
  open,
  discount,
  isSubmitting = false,
  onClose,
  onSubmit,
}: {
  open: boolean;
  discount: DiscountRow | null;
  isSubmitting?: boolean;
  onClose: () => void;
  onSubmit: (input: { customerLabel: string; reason: string }) => void;
}) {
  const [customerLabel, setCustomerLabel] = useState('');
  const [reason, setReason] = useState('');

  useEffect(() => {
    if (open && discount) {
      setCustomerLabel(discount.couponName ?? '');
      setReason(discount.reason ?? '');
    }
  }, [open, discount]);

  if (!discount) return null;

  const labelValid = customerLabel.trim().length > 0;
  const reasonValid = reason.trim().length >= 3;
  const unchanged =
    customerLabel.trim() === (discount.couponName ?? '') &&
    reason.trim() === (discount.reason ?? '');
  const canSubmit = labelValid && reasonValid && !unchanged && !isSubmitting;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Edit discount labels</DialogTitle>
      <DialogContent>
        <Alert severity="info" sx={{ mb: 2 }}>
          Only the labels change. {describeDiscount(discount)} stays exactly as it is —
          the customer keeps the same discount and their billing is untouched.
        </Alert>

        <Stack spacing={2}>
          <TextField
            label="Reason (internal)"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            fullWidth
            multiline
            minRows={2}
            autoFocus
            error={reason.length > 0 && !reasonValid}
            helperText={
              reason.length > 0 && !reasonValid
                ? 'Give at least a few words'
                : 'Only staff see this'
            }
          />

          <TextField
            label="Label on customer invoice"
            value={customerLabel}
            onChange={(e) => setCustomerLabel(e.target.value)}
            fullWidth
            slotProps={{ htmlInput: { maxLength: 40 } }}
            error={!labelValid}
            helperText={
              !labelValid
                ? 'Required — this appears on their Stripe invoice'
                : 'Changing this changes what they see on their next invoice'
            }
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button variant="contained" disabled={!canSubmit} onClick={() =>
          onSubmit({ customerLabel: customerLabel.trim(), reason: reason.trim() })
        }>
          Save labels
        </Button>
      </DialogActions>
    </Dialog>
  );
}
